import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { msalInstance } from '../../index';

/**
 * Azure AD API scope for internal API access
 */
const API_SCOPE = `${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/InternalApi`;

/**
 * Creates an authenticated base query with MSAL token acquisition.
 * This eliminates the need to duplicate authentication logic across all API services.
 *
 * @param baseUrl - The base URL for the API service
 * @returns A configured fetchBaseQuery with automatic token injection
 */
export const createAuthenticatedBaseQuery = (
  baseUrl: string | undefined
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  // An undefined baseUrl makes fetchBaseQuery resolve endpoints against the page origin, so calls
  // silently hit the web app itself and come back as index.html instead of failing loudly.
  if (!baseUrl && process.env.NODE_ENV !== 'production') {
    console.error(
      'API base URL is undefined - requests will resolve against the web app origin and return HTML. '
      + 'Check the REACT_APP_*_API_BASE_URL name in API_BASE_URLS matches the one the env/CI defines.',
    );
  }

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      try {
        const account = msalInstance.getActiveAccount();
        if (account) {
          const response = await msalInstance.acquireTokenSilent({
            account,
            scopes: [API_SCOPE],
          });
          headers.set('Authorization', `Bearer ${response.accessToken}`);
        }
      } catch (e) {
        // Token acquisition failed - request will proceed without auth header
        // This allows the API to return a proper 401 response
        console.warn('Failed to acquire token silently:', e);
      }
      return headers;
    },
  });
};

/**
 * Environment variable names for API base URLs.
 * Centralized here for consistency and easy reference.
 */
export const API_BASE_URLS = {
  accounts: process.env.REACT_APP_Accounts_API_BASE_URL,
  appeals: process.env.REACT_APP_Appeals_API_BASE_URL,
  caseManagement: process.env.REACT_APP_CaseManagements_API_BASE_URL,
  configuration: process.env.REACT_APP_Configuration_API_BASE_URL,
  contracts: process.env.REACT_APP_Contracts_API_BASE_URL,
  encounters: process.env.REACT_APP_Encounters_API_BASE_URL,
  verifications: process.env.REACT_APP_Verifications_API_BASE_URL,
  agents: process.env.REACT_APP_Agents_API_BASE_URL,
  profile: process.env.REACT_APP_Profile_API_BASE_URL,
  refunds: process.env.REACT_APP_Refunds_API_BASE_URL,
  registration: process.env.REACT_APP_Registration_API_BASE_URL,
  writeoffs: process.env.REACT_APP_Writeoffs_API_BASE_URL,
  laborManagement: process.env.REACT_APP_LaborManagement_API_BASE_URL,
  medNec: process.env.REACT_APP_MedNec_API_BASE_URL,
  claims: process.env.REACT_APP_Claims_API_BASE_URL,
  denials: process.env.REACT_APP_Denials_API_BASE_URL,
} as const;
